import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useToast } from '@/components/ui/use-toast';
import { useGymAdmin } from '../context/gym-admin-context';
import { cragRoutesService } from '../../services/crag-routes';
import type {
  CragRoute,
  CragRouteGrade,
  CragRouteModelFile,
  CragRouteSector,
  ModelLine,
} from '../../types/crag-route';
import type { RouteLine } from '../../types/climb';
import { DEFAULT_ROUTE_THICKNESS, isDrawableLine } from '../../components/climbs/route-line';
import { LineActions } from '../../components/climbs/photo-editor/PhotoLineControls';
import type { CragRouteModelApi, CragRouteModelRoute } from '../../components/crag/CragRouteModel';
import SectorModelCard from '../components/crag-routes/SectorModelCard';
import SectorViewLimits, { type ModelTriedLimits } from '../components/crag-routes/SectorViewLimits';
import { isDrawableModelLine, sameCamera, useModelLine } from '../../components/crag/useModelLine';
import { ScanLineEditor } from '../components/scans/ScanLineEditor';
import { ScanEditorLayout, type ScanEditorChrome } from '../components/scans/ScanEditorLayout';
import { ColumnResizer } from '../components/scans/ColumnResizer';
import {
  DEFAULT_PANEL_WIDTH,
  MAX_PANEL_WIDTH,
  MIN_PANEL_WIDTH,
  PANEL_WIDTH_STORAGE_KEY,
  useStoredWidth,
} from '../components/scans/column-width';
import {
  DEFAULT_ROUTES_WIDTH,
  DEFAULT_SECTORS_WIDTH,
  MAX_ROUTES_WIDTH,
  MAX_SECTORS_WIDTH,
  MIN_ROUTES_WIDTH,
  MIN_SECTORS_WIDTH,
  ROUTES_WIDTH_STORAGE_KEY,
  SECTORS_WIDTH_STORAGE_KEY,
} from '../components/crag-routes/column-width';
import { SectorColumn } from '../components/crag-routes/SectorColumn';
import {
  RouteDragPreview,
  RouteListColumn,
  type RowClickModifiers,
} from '../components/crag-routes/RouteListColumn';
import { sectorFromDropId } from '../components/crag-routes/drop-targets';
import {
  EMPTY_SELECTION,
  routesToMove,
  selectionAfterClick,
} from '../components/crag-routes/route-selection';
import { CragRouteForm, type CragRouteDraft } from '../components/crag-routes/CragRouteForm';
import { SectorLinePreviews } from '../components/crag-routes/SectorLinePreviews';
import { SectorSettingsPanel, type SectorSettings } from '../components/crag-routes/SectorSettingsPanel';
import { nearbyRouteLengths, parseRouteLength } from '../components/crag-routes/route-length';
import { zonesService } from '../../services/zones';
import {
  readCragRouteSelection,
  routeToParams,
  sectorToParams,
  viewToParams,
  type CragRouteView,
} from '../components/crag-routes/crag-route-url';

// three.js and the model: only somebody who switches to the 3D view pays for them.
const CragRouteModel = lazy(() => import('../../components/crag/CragRouteModel'));

/** The id a route being created carries until the server gives it a real one. */
const NEW_ROUTE = 'new';

/** The window, less the page's chrome and the view switch above the model. */
const MODEL_CANVAS_HEIGHT = 'h-[calc(100svh-10rem)] min-h-96';

const EMPTY_DRAFT: CragRouteDraft = {
  name: '',
  gradeId: '',
  routeSetter: null,
  description: '',
  length: '',
};

function draftFromRoute(route: CragRoute): CragRouteDraft {
  return {
    name: route.name ?? '',
    gradeId: route.grade_id ?? '',
    routeSetter: route.route_setter,
    description: route.description ?? '',
    length: route.length === null || route.length === undefined ? '' : String(route.length),
  };
}

/**
 * The crag's topo, built one sector at a time.
 *
 * Four columns, left to right in the order the work is done: which cliff, its
 * routes in the order they are walked past, the cliff's photo with the line of
 * the open route on it, and the tools that draw that line with the route's own
 * details under them.
 *
 * A crag has no wall to photograph route by route, so **the sector's photo is
 * the picture every one of its routes is drawn on** — one shot of the cliff,
 * a dozen lines over it. That is why the photo column is keyed to the sector
 * and not to the route: opening another line does not reload the picture, it
 * just draws something different on it.
 *
 * The editor itself is the scans page's `ScanLineEditor`, unchanged. A route
 * outdoors is marked exactly as a route indoors is — the holds are too small to
 * tell apart from the ground either way — so this page composes that editor
 * rather than growing a second one that would drift from it.
 */
export default function CragRoutesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { selectedGymSlug: cragSlug, hasAnyRole } = useGymAdmin();
  // The file is the crag's admins' to change, here as on /model-3d.
  const canManageModelFile = hasAnyRole(['admin']);

  // What is open lives in the URL, so a half-drawn sector can be pasted to
  // whoever is bolting it and a reload comes back to the same cliff.
  const [searchParams, setSearchParams] = useSearchParams();
  const { sectorId: selectedSectorId, routeId: selectedRouteId, view: requestedView } = readCragRouteSelection(searchParams);

  const [sectors, setSectors] = useState<CragRouteSector[]>([]);
  const [grades, setGrades] = useState<CragRouteGrade[]>([]);
  const [routes, setRoutes] = useState<CragRoute[]>([]);
  // The crag's routes that are in no sector at all, which come back whichever
  // sector is open: they belong to none, so no sector's list would ever show
  // them and nobody could put them anywhere.
  const [unassigned, setUnassigned] = useState<CragRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [model3d, setModel3d] = useState<CragRouteModelFile | null>(null);
  // Which sector `routes` belongs to. Between opening a sector and its routes
  // arriving the list still holds the last one's, and the 3D view frames the
  // sector on its lines.
  const [loadedSectorId, setLoadedSectorId] = useState<string | null>(null);

  // Which rows are picked out, for the one thing that is done to several routes
  // at once: moving them to a sector. Kept apart from the open route (which
  // lives in the URL) because they answer different questions — what the editor
  // is showing, and what a drag would carry.
  const [selection, setSelection] = useState(EMPTY_SELECTION);

  // The route being edited, as it stands in the panel: its fields and its line.
  // Held here rather than in the form because the drawing and the fields are
  // saved together, and the editor two columns over owns half of it.
  const [draft, setDraft] = useState<CragRouteDraft>(EMPTY_DRAFT);
  const [line, setLine] = useState<RouteLine | null>(null);
  const [modelLine, setModelLine] = useState<ModelLine | null>(null);
  // Sent only if changed: a tab left open across a model's replacement refuses a
  // line on the old model, which must not stop anybody renaming a route.
  const modelLineTouchedRef = useRef(false);
  const modelApi = useRef<CragRouteModelApi | null>(null);
  // The limits being tried out as a member would meet them. The panel's camera is
  // otherwise free, because drawing a line needs to get closer than a member may.
  const [triedLimits, setTriedLimits] = useState<ModelTriedLimits | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // The sector's own settings, edited in the panel whenever no route is open.
  const [sectorSettings, setSectorSettings] = useState<SectorSettings | null>(null);
  const [sectorPhoto, setSectorPhoto] = useState<{ signedId: string; previewUrl: string } | null>(null);
  const [savingSector, setSavingSector] = useState(false);
  const [sectorError, setSectorError] = useState<string | null>(null);

  // The photo's natural size, measured once per sector: everything drawn on it
  // is expressed against that, which is what lets the member app redraw it.
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  const [sectorsWidth, setSectorsWidth] = useStoredWidth(
    SECTORS_WIDTH_STORAGE_KEY, DEFAULT_SECTORS_WIDTH, MIN_SECTORS_WIDTH, MAX_SECTORS_WIDTH
  );
  const [routesWidth, setRoutesWidth] = useStoredWidth(
    ROUTES_WIDTH_STORAGE_KEY, DEFAULT_ROUTES_WIDTH, MIN_ROUTES_WIDTH, MAX_ROUTES_WIDTH
  );
  const [panelWidth, setPanelWidth] = useStoredWidth(
    PANEL_WIDTH_STORAGE_KEY, DEFAULT_PANEL_WIDTH, MIN_PANEL_WIDTH, MAX_PANEL_WIDTH
  );

  // ⚠️ The loaders must not depend on `t`/`toast`: an effect that rebuilds when
  // those change identity is an infinite render loop, not a stale closure. The
  // same rule the scans page runs under, and for the same reason.
  const notifyRef = useRef({ t, toast });
  notifyRef.current = { t, toast };

  const selectSector = useCallback(
    (sectorId: string) => setSearchParams(sectorToParams(sectorId, searchParams)),
    [searchParams, setSearchParams]
  );

  const selectView = useCallback(
    (next: CragRouteView) => setSearchParams(viewToParams(next, searchParams), { replace: true }),
    [searchParams, setSearchParams]
  );

  const selectRoute = useCallback(
    (routeId: string | null) =>
      // Closing one is not a place to come back to, so it rewrites rather than
      // stacks; opening one is, so it does not.
      setSearchParams(routeToParams(routeId, searchParams), { replace: routeId === null }),
    [searchParams, setSearchParams]
  );

  const load = useCallback(
    async (slug: string, sectorId: string | null, { quiet = false } = {}) => {
      if (!quiet) setRoutesLoading(true);
      try {
        const data = await cragRoutesService.list(slug, sectorId);
        setSectors(data.sectors);
        setGrades(data.grades);
        setRoutes(data.routes);
        setUnassigned(data.unassigned);
        setModel3d(data.model3d);
        setLoadedSectorId(sectorId);
      } catch (error) {
        const { t: translate, toast: notify } = notifyRef.current;
        notify({
          title: translate('cragAdmin.routes.loadError'),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
        setRoutesLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!cragSlug) return;
    load(cragSlug, selectedSectorId);
  }, [cragSlug, selectedSectorId, load]);

  // Land on the first sector rather than on an empty page. Replaces, so Back
  // does not walk into the bare URL that redirected.
  useEffect(() => {
    if (selectedSectorId || sectors.length === 0) return;
    setSearchParams(sectorToParams(sectors[0].id, searchParams), { replace: true });
  }, [selectedSectorId, sectors, searchParams, setSearchParams]);

  const sector = useMemo(
    () => sectors.find((candidate) => candidate.id === selectedSectorId) ?? null,
    [sectors, selectedSectorId]
  );

  const route = useMemo(
    () => routes.find((candidate) => candidate.id === selectedRouteId) ?? null,
    [routes, selectedRouteId]
  );

  const creating = selectedRouteId === NEW_ROUTE;
  const editing = creating || route !== null;

  // Read off the list rather than off the draft, so the chips are the sector's
  // lines as saved — the neighbours, not what is being typed about this one.
  const nearbyLengths = useMemo(
    () => nearbyRouteLengths(routes, selectedRouteId),
    [routes, selectedRouteId]
  );

  // Fill the panel from whichever route is open — **once per route**. The
  // effect watches the list as well as the id, because a reload arrives at a
  // URL naming a route the list has not fetched yet and there would otherwise
  // be nothing to seed from; but once seeded, the draft is the setter's typing,
  // and re-reading the server copy on every list refresh would overwrite what
  // they are in the middle of saying.
  const seededRouteIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedRouteId === null || selectedRouteId === NEW_ROUTE) {
      seededRouteIdRef.current = selectedRouteId;
      setDraft(EMPTY_DRAFT);
      setLine(null);
      setModelLine(null);
      modelLineTouchedRef.current = false;
      setFormError(null);
      return;
    }
    if (seededRouteIdRef.current === selectedRouteId) return;

    const opened = routes.find((candidate) => candidate.id === selectedRouteId);
    if (!opened) return;

    seededRouteIdRef.current = selectedRouteId;
    setDraft(draftFromRoute(opened));
    // A path traced on some other picture — a member's own photo of the route —
    // is not a path on this cliff, so the editor opens with nothing drawn rather
    // than with a scribble over the wrong part of the sector.
    setLine(opened.line_on_sector_photo ? opened.line ?? null : null);
    setModelLine(opened.model_line ?? null);
    modelLineTouchedRef.current = false;
    setFormError(null);
  }, [selectedRouteId, routes]);

  // A new sector is a new picture, so its size has to be measured again — and
  // its own settings, any half-picked photo and whatever was picked out in the
  // list all belong to the cliff being left.
  useEffect(() => {
    setNaturalSize(null);
    setSectorPhoto(null);
    setSectorError(null);
    setSelection(EMPTY_SELECTION);
  }, [selectedSectorId]);

  // Seeded from whichever sector is open, once per sector for the same reason
  // the route draft is: while it is open these fields are the staff's typing.
  const seededSectorIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sector) return;
    if (seededSectorIdRef.current === sector.id) return;

    seededSectorIdRef.current = sector.id;
    setSectorSettings({
      thickness: sector.line_thickness,
      corridor: sector.line_corridor,
      exposure: sector.exposure ?? '',
    });
  }, [sector]);

  /** "Save this view", its limits, and forgetting either: applied at once, it is not a form. */
  const saveModelView = useCallback(
    async (view: Parameters<typeof zonesService.updateZone>[2]['model_view']) => {
      if (!cragSlug || !sector) return false;

      try {
        await zonesService.updateZone(cragSlug, sector.id, { model_view: view }, 'crag');
        await load(cragSlug, selectedSectorId, { quiet: true });
        toast({ title: t('gymAdmin.saved') });
        return true;
      } catch (error) {
        toast({
          title: t('errors.unexpectedError'),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
        return false;
      }
    },
    [cragSlug, sector, selectedSectorId, load, toast, t]
  );

  const handleSaveSector = useCallback(async () => {
    if (!cragSlug || !sector || !sectorSettings) return;

    setSavingSector(true);
    setSectorError(null);
    try {
      await zonesService.updateZone(
        cragSlug,
        sector.id,
        {
          line_thickness: sectorSettings.thickness,
          line_corridor: sectorSettings.corridor,
          exposure: sectorSettings.exposure,
          ...(sectorPhoto?.signedId ? { image_signed_id: sectorPhoto.signedId } : {}),
        },
        'crag'
      );

      // Re-read rather than patch: saving the style restamps every route of the
      // sector, so the lines this page is drawing have changed under it.
      setSectorPhoto(null);
      seededSectorIdRef.current = null;
      await load(cragSlug, selectedSectorId, { quiet: true });
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      setSectorError(error instanceof Error ? error.message : t('errors.unexpectedError'));
    } finally {
      setSavingSector(false);
    }
  }, [cragSlug, sector, sectorSettings, sectorPhoto, selectedSectorId, load, toast, t]);

  const handleSave = useCallback(async () => {
    if (!cragSlug) return;

    setSaving(true);
    setFormError(null);
    try {
      // Saving from the 3D view saves the camera too: it is how this route opens from
      // now on, and looking at the line from a better place is a change like any other.
      const camera = requestedView === 'model' ? modelApi.current?.currentView() ?? null : null;
      const cameraMoved = !!camera && isDrawableModelLine(modelLine) && !sameCamera(camera, modelLine.view);
      const modelLineChanged = modelLineTouchedRef.current || cameraMoved;
      const modelLineToSave = modelLine && camera ? { ...modelLine, view: camera } : modelLine;

      const payload = {
        name: draft.name.trim(),
        grade_id: draft.gradeId || null,
        zone_id: selectedSectorId,
        route_setter_id: draft.routeSetter ? String(draft.routeSetter.id) : null,
        description: draft.description.trim() || null,
        length: parseRouteLength(draft.length).value,
        line,
        ...(modelLineChanged ? { model_line: modelLineToSave } : {}),
      };

      const saved = creating
        ? await cragRoutesService.create(cragSlug, payload)
        : await cragRoutesService.update(cragSlug, route!.id, payload);

      // The list is re-read rather than patched: a create changes the sector's
      // count and the topo's numbering, which the server owns.
      // What the server kept, camera included: the next save compares against it.
      if (modelLineChanged) setModelLine(saved.model_line ?? null);
      modelLineTouchedRef.current = false;
      await load(cragSlug, selectedSectorId, { quiet: true });
      if (creating) selectRoute(saved.id);

      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : t('errors.unexpectedError'));
    } finally {
      setSaving(false);
    }
  }, [cragSlug, draft, line, modelLine, requestedView, selectedSectorId, creating, route, load, selectRoute, toast, t]);

  const handleDelete = useCallback(async () => {
    if (!cragSlug || !route) return;
    if (!window.confirm(t('cragAdmin.routes.deleteConfirm', { name: route.name }))) return;

    setDeleting(true);
    try {
      await cragRoutesService.remove(cragSlug, route.id);
      selectRoute(null);
      await load(cragSlug, selectedSectorId, { quiet: true });
      toast({ title: t('cragAdmin.routes.deleted', { name: route.name }) });
    } catch (error) {
      toast({
        title: t('cragAdmin.routes.deleteError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  }, [cragSlug, route, selectedSectorId, load, selectRoute, toast, t]);

  // Optimistic, with the previous order kept to put back: dropping a card and
  // watching it spring back to where it was after a round trip reads as a bug.
  const handleReorder = useCallback(
    async (next: CragRoute[]) => {
      if (!cragSlug) return;

      const previous = routes;
      setRoutes(next);
      try {
        await cragRoutesService.reorder(cragSlug, next.map((candidate) => candidate.id));
      } catch (error) {
        setRoutes(previous);
        toast({
          title: t('cragAdmin.routes.reorderError'),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
      }
    },
    [cragSlug, routes, toast, t]
  );

  /**
   * Filing routes under a cliff — what dropping a card, or a whole selection,
   * on a sector does. Optimistic for the same reason the reorder is, and then
   * re-read: the sectors' counts and the topo's numbering are the server's.
   */
  const handleMoveToSector = useCallback(
    async (sectorId: string, routeIds: string[]) => {
      if (!cragSlug) return;

      const known = [...routes, ...unassigned];
      // A card let go of over the sector it is already in is a no-op, not an
      // error: a drop lands wherever the pointer happened to be.
      const moving = routeIds.filter((id) =>
        known.some((candidate) => candidate.id === id && candidate.zone_id !== sectorId)
      );
      if (moving.length === 0) return;

      const previousRoutes = routes;
      const previousUnassigned = unassigned;
      const moved = known.filter((candidate) => moving.includes(candidate.id));
      const staying = (list: CragRoute[]) => list.filter((candidate) => !moving.includes(candidate.id));

      setRoutes(
        sectorId === selectedSectorId
          ? // Appended rather than slotted in: positions are the crag's to hand
            // out, and the re-read below is what puts these where they belong.
            [...staying(routes), ...moved.map((route) => ({ ...route, zone_id: sectorId }))]
          : staying(routes)
      );
      setUnassigned(staying(unassigned));
      setSelection(EMPTY_SELECTION);
      // The editor draws on the open sector's photo, so a route carried off to
      // another cliff cannot stay open in it.
      if (selectedRouteId && moving.includes(selectedRouteId) && sectorId !== selectedSectorId) {
        selectRoute(null);
      }

      try {
        await cragRoutesService.move(cragSlug, moving, sectorId);
        await load(cragSlug, selectedSectorId, { quiet: true });
        toast({
          title: t('cragAdmin.routes.moved', {
            count: moving.length,
            name: sectors.find((candidate) => candidate.id === sectorId)?.name ?? '',
          }),
        });
      } catch (error) {
        setRoutes(previousRoutes);
        setUnassigned(previousUnassigned);
        toast({
          title: t('cragAdmin.routes.moveError'),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
      }
    },
    [cragSlug, routes, unassigned, sectors, selectedSectorId, selectedRouteId, selectRoute, load, toast, t]
  );

  /**
   * A click on a row does two things at once, as it does in any file list: it
   * picks the row out, and — plainly, on a route that has a sector — it opens
   * it. ⌘/Ctrl and Shift only ever pick, because opening is one route at a time.
   *
   * A route in no sector is never opened: the editor draws on the sector's
   * photo, and there is no sector here whose photo this route's line would
   * belong to. It is picked, dragged onto a cliff, and edited from there.
   */
  const handleRowClick = useCallback(
    (routeId: string, { additive, range }: RowClickModifiers) => {
      const unfiled = unassigned.some((candidate) => candidate.id === routeId);
      const order = (unfiled ? unassigned : routes).map((candidate) => candidate.id);

      setSelection((current) => selectionAfterClick(current, { routeId, additive, range, order }));

      if (!additive && !range && !unfiled) selectRoute(routeId);
    },
    [routes, unassigned, selectRoute]
  );

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so a press that never moved
    // still reaches the button underneath.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Two kinds of target in one context: the rows of the topo, which a card is
  // dropped *between*, and the sectors, which it is dropped *into*. What is
  // under the pointer decides, and only where it is over nothing at all does
  // the nearest row win — which is what keeps a reorder working when the card
  // is dragged past the end of the list.
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const under = pointerWithin(args);

    return under.length > 0 ? under : closestCenter(args);
  }, []);

  // Which card is under the pointer, so the overlay has something to draw. It
  // cannot be read off the row: the unfiled routes sit in a box that scrolls,
  // and a card dragged out of a scrolling box is clipped by it.
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const dragging = useMemo(
    () => [...routes, ...unassigned].find((candidate) => candidate.id === draggingId) ?? null,
    [routes, unassigned, draggingId]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingId(null);

      const { active, over } = event;
      if (!over) return;

      const draggedId = String(active.id);
      const sectorId = sectorFromDropId(over.id);
      if (sectorId !== null) {
        void handleMoveToSector(sectorId, routesToMove(selection, draggedId));
        return;
      }

      // Dropped on another route: the topo's order. Nothing to do when the card
      // dragged is not part of this topo — an unfiled route has no place in it.
      const overId = String(over.id);
      if (overId === draggedId) return;

      const from = routes.findIndex((route) => route.id === draggedId);
      const to = routes.findIndex((route) => route.id === overId);
      if (from === -1 || to === -1) return;

      void handleReorder(arrayMove(routes, from, to));
    },
    [routes, selection, handleMoveToSector, handleReorder]
  );

  // The model this sector is read on: its own file, else the crag's.
  const sectorModel = sector?.model_3d ?? model3d;
  // The URL may ask for a model nothing here has. An admin gets the 3D view regardless,
  // because that is where a sector's first model is uploaded.
  const modelViewAvailable = !!sectorModel || canManageModelFile;
  const view: CragRouteView = requestedView === 'model' && modelViewAvailable ? 'model' : 'photo';

  const modelEditor = useModelLine({
    frame: sectorModel?.frame ?? null,
    line: modelLine,
    onChange: (next) => {
      modelLineTouchedRef.current = true;
      setModelLine(next);
    },
  });

  // A selection is an index into one route's line.
  const { clearSelection: clearModelSelection } = modelEditor;
  useEffect(() => clearModelSelection(), [selectedRouteId, clearModelSelection]);

  const modelRoutes = useMemo(
    () =>
      routes.flatMap((candidate): CragRouteModelRoute[] =>
        candidate.id !== selectedRouteId && isDrawableModelLine(candidate.model_line)
          ? [{ id: candidate.id, name: candidate.name, model_line: candidate.model_line }]
          : []
      ),
    [routes, selectedRouteId]
  );

  const hasLineInView = useCallback(
    (candidate: CragRoute) =>
      view === 'model'
        ? isDrawableModelLine(candidate.model_line)
        : isDrawableLine(candidate.line) && candidate.line_on_sector_photo,
    [view]
  );

  // A route with a line opens where it was saved from, or else on its line. A route
  // with none leaves the camera alone, so a line is started from wherever somebody
  // had got to. With no route open, the sector: its saved view, or all of its lines.
  // Saved values only: a line being drawn must not move the camera under the pen.
  const modelFraming = useMemo(() => {
    if (!sector || loadedSectorId !== sector.id) return null;

    if (route && isDrawableModelLine(route.model_line)) {
      return {
        key: `route:${route.id}`,
        view: route.model_line.view ?? null,
        points: route.model_line.points,
      };
    }

    return {
      key: `sector:${sector.id}`,
      view: sector.model_view ?? null,
      points: routes.flatMap((candidate) => candidate.model_line?.points ?? []),
    };
  }, [sector, route, loadedSectorId, routes]);

  const chrome: ScanEditorChrome = {
    panelWidth,
    onPanelWidthChange: setPanelWidth,
    imageLabel: t('cragAdmin.routes.imageLabel'),
    panelLabel: t('cragAdmin.routes.panelLabel'),
    // Only where there is a model, or one can be uploaded. Nothing else on the page
    // changes with it, so a half-drawn line and a half-typed form stay where they are.
    notice: modelViewAvailable ? (
      <ToggleGroup
        type="single"
        value={view}
        onValueChange={(next) => {
          if (next) selectView(next as CragRouteView);
        }}
        aria-label={t('cragAdmin.routes.viewLabel')}
        className="inline-flex h-8 items-center gap-1 rounded-md border bg-muted/40 p-0.5"
      >
        {(['photo', 'model'] as const).map((option) => (
          <ToggleGroupItem
            key={option}
            value={option}
            className="inline-flex h-7 items-center rounded px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
          >
            {t(option === 'photo' ? 'cragAdmin.routes.viewPhoto' : 'cragAdmin.routes.viewModel')}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    ) : undefined,
    // The panel says one thing at a time: the route being worked on, or — when
    // none is open — the sector itself, which is what the photo behind it is
    // showing anyway.
    tools: editing ? (
      <CragRouteForm
        draft={draft}
        onDraftChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        grades={grades}
        nearbyLengths={nearbyLengths}
        cragSlug={cragSlug ?? ''}
        onSave={handleSave}
        onDelete={creating ? undefined : handleDelete}
        saving={saving}
        deleting={deleting}
        error={formError}
      />
    ) : sector && sectorSettings ? (
      <SectorSettingsPanel
        sector={sector}
        settings={sectorSettings}
        onSettingsChange={(patch) => setSectorSettings((current) => ({ ...current!, ...patch }))}
        onSave={handleSaveSector}
        onImageUploaded={(signedId, previewUrl) =>
          setSectorPhoto((current) => ({
            signedId: signedId || current?.signedId || '',
            previewUrl: previewUrl || current?.previewUrl || '',
          }))
        }
        pendingImageUrl={sectorPhoto?.previewUrl || null}
        saving={savingSector}
        error={sectorError}
        drawing={
          canManageModelFile && cragSlug
            ? {
                crag: cragSlug,
                // A new picture, so its size has to be measured again.
                onChanged: async () => {
                  setNaturalSize(null);
                  await load(cragSlug, selectedSectorId, { quiet: true });
                },
              }
            : undefined
        }
      />
    ) : null,
  };

  // A photo picked in the panel but not yet saved is still the picture to draw
  // on — the sector had none, and waiting for a round trip to show it would
  // read as the upload having failed.
  const photoUrl = sectorPhoto?.previewUrl || sector?.image_url || null;

  // The style is the sector's, so what the editor draws has to be the sector's
  // too — including while the sliders are being moved, before anything is
  // saved. The route contributes its points and nothing else.
  const styledLine = line
    ? { ...line, corridor: sectorSettings?.corridor ?? 0, thickness: sectorSettings?.thickness }
    : null;

  return (
    // No heading: the sidebar already says which section this is, and the four
    // columns need every row of the window they can get.
    <div className="flex h-[calc(100svh-5rem)] flex-col gap-3">
      {/* One drag context over the first two columns, because a route is moved
          by dropping it on a cliff in the other one: the cards and the sectors
          they are filed under have to be draggable and droppable in the same
          context, or there is nothing to drop onto. */}
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={(event) => setDraggingId(String(event.active.id))}
        onDragCancel={() => setDraggingId(null)}
        onDragEnd={handleDragEnd}
      >
        {/* The photo is the elastic column (`1fr`); the sectors, the routes and
            the panel are the widths dragged out for them, so every pixel they give
            back goes to the picture. Below `lg` it all stacks and the dividers,
            having nothing to divide, hide themselves. */}
        <div
          className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:gap-0 lg:[grid-template-columns:var(--sectors-width)_0.75rem_var(--routes-width)_0.75rem_minmax(0,1fr)_0.75rem_var(--panel-width)]"
          style={
            {
              '--sectors-width': `${sectorsWidth}px`,
              '--routes-width': `${routesWidth}px`,
              '--panel-width': `${panelWidth}px`,
            } as React.CSSProperties
          }
        >
          <section
            className="min-h-0 overflow-y-auto rounded-md border"
            aria-label={t('cragAdmin.routes.sectorsLabel')}
            data-testid="crag-sectors-column"
          >
            <SectorColumn
              sectors={sectors}
              selectedId={selectedSectorId}
              onSelect={selectSector}
              loading={loading}
            />
          </section>

          <ColumnResizer
            width={sectorsWidth}
            onWidthChange={setSectorsWidth}
            min={MIN_SECTORS_WIDTH}
            max={MAX_SECTORS_WIDTH}
            label={t('cragAdmin.routes.resizeSectors')}
            side="left"
          />

          <section
            className="min-h-0 overflow-y-auto rounded-md border"
            aria-label={t('cragAdmin.routes.routesLabel')}
            data-testid="crag-routes-column"
          >
            <RouteListColumn
              routes={routes}
              unassigned={unassigned}
              selectedId={selectedRouteId}
              pickedIds={selection.ids}
              onRowClick={handleRowClick}
              onClearSelection={() => setSelection(EMPTY_SELECTION)}
              onAdd={() => selectRoute(NEW_ROUTE)}
              hasLine={hasLineInView}
              loading={routesLoading}
              disabled={!selectedSectorId}
            />
          </section>

          <ColumnResizer
            width={routesWidth}
            onWidthChange={setRoutesWidth}
            min={MIN_ROUTES_WIDTH}
            max={MAX_ROUTES_WIDTH}
            label={t('cragAdmin.routes.resizeRoutes')}
            side="left"
          />

          {/* The photo and the panel are two of this grid's columns, so whatever
              fills them has to be a pair of siblings here — hence ScanEditorLayout
              rendering a fragment, and every branch below producing one. */}
          {!sector ? (
            <PlaceholderColumns>
              <p className="text-sm text-muted-foreground">{t('cragAdmin.routes.selectSector')}</p>
            </PlaceholderColumns>
          ) : view === 'model' ? (
            // One branch, with a route open or not: a second one would remount the
            // viewer, and download the model again, at every click on a route. Ahead
            // of the photo's, because the model does not need the sector to have one.
            <ScanEditorLayout
              key="model"
              canvas={
                sectorModel ? (
                  <Suspense fallback={<Skeleton className={MODEL_CANVAS_HEIGHT} />}>
                    <CragRouteModel
                      url={sectorModel.url}
                      routes={modelRoutes}
                      editor={editing ? modelEditor : null}
                      framing={modelFraming}
                      viewLimits={triedLimits}
                      onSelectRoute={selectRoute}
                      apiRef={modelApi}
                      className={MODEL_CANVAS_HEIGHT}
                    />
                  </Suspense>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('cragAdmin.routes.sectorModel.none')}</p>
                )
              }
              {...chrome}
            >
              {editing ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t('gymAdmin.scans.linePoints', { count: modelEditor.points.length })}
                  </p>
                  <LineActions
                    editor={modelEditor}
                    labels={{
                      deletePoint: t('gymAdmin.scans.deletePoint'),
                      undoPoint: t('gymAdmin.scans.undoPoint'),
                      clearLine: t('gymAdmin.scans.clearLine'),
                    }}
                  />
                  <p className="text-xs text-muted-foreground">{t('cragAdmin.routes.modelLineHint')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">{t('cragAdmin.routes.selectRoute')}</p>
                  {sectorModel && (
                    <>
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const current = modelApi.current?.currentView();
                              if (current) void saveModelView(current);
                            }}
                          >
                            {t('cragAdmin.routes.saveModelView')}
                          </Button>
                          {sector.model_view && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (!sector.model_view?.limits || window.confirm(t('cragAdmin.routes.limits.forgetViewConfirm'))) {
                                  void saveModelView(null);
                                }
                              }}
                            >
                              {t('cragAdmin.routes.forgetModelView')}
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{t('cragAdmin.routes.modelViewHint')}</p>
                      </div>
                      {/* Per sector and per file: the draft is measured from this view. */}
                      <SectorViewLimits
                        key={`${sector.id}:${sectorModel.frame}`}
                        savedView={sector.model_view ?? null}
                        viewerApi={modelApi}
                        onSave={saveModelView}
                        onTry={setTriedLimits}
                      />
                    </>
                  )}
                  <SectorModelCard
                    sector={sector}
                    crag={cragSlug ?? ''}
                    hasCragModel={!!model3d}
                    canManage={canManageModelFile}
                    onChanged={() => (cragSlug ? load(cragSlug, selectedSectorId, { quiet: true }) : Promise.resolve())}
                  />
                </div>
              )}
            </ScanEditorLayout>
          ) : !photoUrl ? (
            // A sector nobody has photographed yet still has routes worth naming
            // and grading, so the panel stays — only the drawing is out of reach.
            <ScanEditorLayout
              canvas={
                <p className="text-sm text-muted-foreground">{t('cragAdmin.routes.sectorHasNoPhoto')}</p>
              }
              {...chrome}
            >
              {!editing && (
                <p className="text-sm text-muted-foreground">{t('cragAdmin.routes.selectRoute')}</p>
              )}
            </ScanEditorLayout>
          ) : !naturalSize ? (
            <PlaceholderColumns>
              {chrome.notice}
              <img
                src={photoUrl}
                alt={sector?.name ?? ''}
                className="w-full rounded-md border"
                onLoad={(event) =>
                  setNaturalSize({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  })
                }
              />
            </PlaceholderColumns>
          ) : editing ? (
            <ScanLineEditor
              imageUrl={photoUrl}
              naturalWidth={naturalSize.width}
              naturalHeight={naturalSize.height}
              line={styledLine}
              onChange={setLine}
              // Neither is the route's to set: how a cliff's lines are drawn is a
              // property of the picture they are drawn on, so both live on the
              // sector and are set in the panel with no route open.
              allowCorridor={false}
              allowThickness={false}
              chrome={chrome}
            />
          ) : (
            // Nothing open: the sector's lines as they stand, each one a way into
            // the route it belongs to.
            <ScanEditorLayout
              canvas={
                <div className="relative">
                  <img src={photoUrl} alt={sector?.name ?? ''} className="block w-full rounded-md border" />
                  <SectorLinePreviews
                    routes={routes}
                    naturalWidth={naturalSize.width}
                    naturalHeight={naturalSize.height}
                    thickness={sectorSettings?.thickness ?? DEFAULT_ROUTE_THICKNESS}
                    onSelect={selectRoute}
                  />
                </div>
              }
              {...chrome}
            >
              <p className="text-sm text-muted-foreground">{t('cragAdmin.routes.selectRoute')}</p>
            </ScanEditorLayout>
          )}
        </div>

        {/* The width of the column it came from, so what is being carried reads
            as the card it is rather than as a label. */}
        <DragOverlay>
          {dragging ? (
            <div style={{ width: routesWidth - 32 }}>
              <RouteDragPreview route={dragging} count={routesToMove(selection, dragging.id).length} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

/**
 * Stands in for the photo and panel columns before there is anything to draw
 * on. A fragment of two cells rather than one spanning both: the grid's columns
 * are fixed, and a single cell would leave the panel's column empty and the
 * layout jumping as soon as the editor arrives.
 */
function PlaceholderColumns({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <>
      <section
        className="min-h-0 overflow-y-auto rounded-md border p-3"
        aria-label={t('cragAdmin.routes.imageLabel')}
        data-testid="crag-photo-column"
      >
        {children}
      </section>
      {/* No divider: there is nothing to size yet. The column stays so the
          grid's shape does not change under the editor when it loads. */}
      <div aria-hidden />
      <aside
        className="hidden min-h-0 overflow-y-auto rounded-md border p-3 lg:block lg:w-[var(--panel-width)]"
        aria-label={t('cragAdmin.routes.panelLabel')}
        data-testid="crag-panel-column"
      />
    </>
  );
}
